import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { useMe } from '../session.js';

type Step = 'site' | 'verify' | 'alertTest' | 'contentWatch';

// Stored per device; older versions stored "hidden" under the same key, which now means collapsed.
const collapseKey = (accountId: string) => `wg:firstSteps:hidden:${accountId}`;
const readCollapsed = (accountId: string) => {
  try {
    return window.localStorage.getItem(collapseKey(accountId)) === '1';
  } catch {
    return false;
  }
};
const storeCollapsed = (accountId: string, collapsed: boolean) => {
  try {
    if (collapsed) window.localStorage.setItem(collapseKey(accountId), '1');
    else window.localStorage.removeItem(collapseKey(accountId));
  } catch {
    // Private mode: the choice lasts until the page is reloaded.
  }
};

const Done = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M5 12l4 4L19 6" />
  </svg>
);
const Open = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="8" />
  </svg>
);

/**
 * First steps of a new account (T-65, design/pages/app/overview.html): add a site, confirm
 * ownership, send a test alert, watch a phone number or text. Shown on the overview until done;
 * it can be collapsed into one line with the progress and opened again (remembered on this
 * device). T-92: the current step is explained.
 */
export function FirstSteps() {
  const { t } = useTranslation();
  const accountId = useMe().data?.account.id ?? '';
  // null: follow the stored choice (read on every render: the account id arrives after the first).
  const [choice, setChoice] = useState<boolean | null>(null);
  const collapsed = choice ?? (accountId !== '' && readCollapsed(accountId));
  const state = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => unwrap(api.GET('/onboarding')),
  });
  const data = state.data;
  if (!data || data.completed) return null;

  const site = data.siteId;
  const steps: { key: Step; done: boolean; to: string }[] = [
    { key: 'site', done: data.steps.site, to: '/sites/new' },
    {
      key: 'verify',
      done: data.steps.verify,
      to: site ? `/sites/${site}/settings` : '/sites/new',
    },
    { key: 'alertTest', done: data.steps.alertTest, to: '/notifications' },
    ...(data.contentWatchAvailable
      ? [
          {
            key: 'contentWatch' as const,
            done: data.steps.contentWatch,
            to: site ? `/sites/${site}/settings` : '/sites/new',
          },
        ]
      : []),
  ];
  const done = steps.filter((s) => s.done).length;
  const toggle = () => {
    storeCollapsed(accountId, !collapsed);
    setChoice(!collapsed);
  };
  // The first open step is the one to do now; it gets its explanation.
  const current = steps.findIndex((s) => !s.done);
  return (
    <section className="steps-card" aria-labelledby="first-steps">
      <div className="t">
        <h2 id="first-steps">{t('firstSteps.title')}</h2>
        <span>
          <span>{t('firstSteps.progress', { done, total: steps.length })}</span> ·{' '}
          <button type="button" onClick={toggle} aria-expanded={!collapsed}>
            {collapsed ? t('firstSteps.expand') : t('firstSteps.collapse')}
          </button>
        </span>
      </div>
      {collapsed ? null : (
        <ol className={steps.length === 3 ? 'n3' : undefined}>
          {steps.map((s, i) => (
            <li key={s.key} className={s.done ? 'done' : 'todo'}>
              {s.done ? <Done /> : <Open />}
              {s.done ? (
                <span>
                  {t(`firstSteps.steps.${s.key}.title`)}
                  <span className="sr-only"> – {t('firstSteps.done')}</span>
                </span>
              ) : (
                <span>
                  <Link to={s.to}>{t(`firstSteps.steps.${s.key}.title`)}</Link>
                  {i === current ? (
                    <span className="why">{t(`firstSteps.steps.${s.key}.text`)}</span>
                  ) : null}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
